import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../services/api';
import './FinalApplicationDispositionModel.css';
import './IntegrationApplicationMasterData.css';

interface DispositionApplication {
  id: string;
  apr_model_id: string;
  apr_model_name: string;
  application_id: string;
  application_id_code: string;
  application_name: string;
  original_4r_recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain';
  overall_score: number;
  final_4r_model: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain' | null;
  created_at: string;
  updated_at: string;
  updated_by?: string;
  changed_by_user_id?: string;
  change_reason?: string;
  transferred_to_integration?: boolean;
  transferred_at?: string;
  transferred_by?: string;
  retain_confirmed?: boolean;
  retain_confirmed_at?: string;
  retain_confirmed_by?: string;
  license_renegotiation?: boolean;
  consolidate?: boolean;
  consolidate_target_app?: string;
  cloud_migration?: boolean;
  replace_app?: boolean;
  replace_target_app?: string;
}

interface ReadyForTransferApplication {
  application_id: string;
  application_id_code: string;
  application_name: string;
  recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain';
  overall_score: number;
  completed_at_formatted: string;
}

interface APRModelGroup {
  modelId: string;
  modelName: string;
  modelStatus: string;
  applications: DispositionApplication[];
}

const FinalApplicationDispositionModel: React.FC = () => {
  const { currentUser } = useAuth();
  const [applications, setApplications] = useState<DispositionApplication[]>([]);
  const [collapsedModels, setCollapsedModels] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{appId: string, field: string} | null>(null);
  const [editingModal, setEditingModal] = useState<{
    appId: string;
    appName: string;
    currentValue: string | null;
    newValue: string;
    reason: string;
    licenseRenegotiation: boolean;
    consolidate: boolean;
    consolidateTargetApp: string;
    cloudMigration: boolean;
    replaceApp: boolean;
    replaceTargetApp: string;
  } | null>(null);
  const [transferModal, setTransferModal] = useState<{
    show: boolean;
    applications: ReadyForTransferApplication[];
    selectedApps: Set<string>;
    loading: boolean;
  }>({
    show: false,
    applications: [],
    selectedApps: new Set(),
    loading: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterOriginal, setFilterOriginal] = useState<string>('All');
  const [filterFinal, setFilterFinal] = useState<string>('All');
  const [sortField, setSortField] = useState<string>('application_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10000);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    totalApplications: 0,
    completedDispositions: 0,
    pendingDispositions: 0,
    distribution: {
      'Retire': 0,
      'Retain': 0,
      'Re-Platform': 0,
      'Re-Design': 0
    }
  });

  const [initialized, setInitialized] = useState(false);

  // Derive modelGroups from applications using useMemo so it updates when applications change
  const modelGroups = React.useMemo(() => {
    // Group applications by APR Model
    const groupedByModel = applications.reduce((acc: Record<string, APRModelGroup>, app: DispositionApplication) => {
      const modelId = app.apr_model_id || 'unknown';
      const modelName = app.apr_model_name || 'Unknown Model';

      if (!acc[modelId]) {
        acc[modelId] = {
          modelId,
          modelName,
          modelStatus: 'Active',
          applications: []
        };
      }
      acc[modelId].applications.push(app);
      return acc;
    }, {});

    // Convert to array and sort by model name
    return (Object.values(groupedByModel) as APRModelGroup[]).sort((a, b) =>
      a.modelName.localeCompare(b.modelName)
    );
  }, [applications]);

  // Initialize disposition data on first load to sync with scored applications
  useEffect(() => {
    const initializeDispositionData = async () => {
      try {
        // Sync any newly scored applications to the disposition table
        await authFetch('/api/final-disposition/initialize', {
          method: 'POST',
          body: JSON.stringify({ createdBy: 'system' })
        });
      } catch (error) {
        console.error('Failed to initialize disposition data:', error);
      } finally {
        setInitialized(true);
      }
    };
    initializeDispositionData();
  }, []); // Run once on component mount

  // Debounce search term so API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (initialized) {
      loadDispositionData();
    }
  }, [initialized, currentPage, pageSize, debouncedSearchTerm, filterFinal, sortField, sortDirection]);

  const loadDispositionData = async () => {
    if (!initialLoadDone) setLoading(true);
    setError(null);

    try {
      const statusFilter = filterFinal === 'All' ? 'all' :
                          filterFinal === 'Pending' ? 'pending' : filterFinal;

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        search: debouncedSearchTerm,
        statusFilter,
        sortBy: sortField,
        sortOrder: sortDirection
      });

      const response = await authFetch(`/api/final-disposition?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch disposition data');
      }

      const result = await response.json();

      if (result.success) {
        setApplications(result.data);
        setTotalCount(result.pagination.total);
        // Map new backend field names to what this page expects
        setStats({
          totalApplications: result.stats.totalApplications || 0,
          completedDispositions: result.stats.dispositionSet || result.stats.completedDispositions || 0,
          pendingDispositions: result.stats.pendingDispositions || 0,
          distribution: result.stats.distribution || { 'Retire': 0, 'Retain': 0, 'Re-Platform': 0, 'Re-Design': 0 }
        });

        // Initially collapse all model groups (on first load only)
        if (collapsedModels.size === 0 && result.data.length > 0) {
          const allModelIds = result.data.reduce((ids: Set<string>, app: DispositionApplication) => {
            ids.add(app.apr_model_id || 'unknown');
            return ids;
          }, new Set<string>());
          setCollapsedModels(allModelIds);
        }
      } else {
        throw new Error(result.message || 'Unknown error occurred');
      }
    } catch (err: any) {
      console.error('Error loading disposition data:', err);
      setError(err.message || 'Failed to load disposition data');
      // Fallback to empty state
      setApplications([]);
      setTotalCount(0);
      setStats({
        totalApplications: 0,
        completedDispositions: 0,
        pendingDispositions: 0,
        distribution: { 'Retire': 0, 'Retain': 0, 'Re-Platform': 0, 'Re-Design': 0 }
      });
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  };

  // Toggle collapse/expand for a single model
  const toggleModelCollapse = (modelId: string) => {
    setCollapsedModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  // Expand all models
  const handleExpandAll = () => {
    setCollapsedModels(new Set());
  };

  // Collapse all models
  const handleCollapseAll = () => {
    const allModelIds = modelGroups.map(group => group.modelId);
    setCollapsedModels(new Set(allModelIds));
  };


  const openEditingModal = (appId: string) => {
    const app = applications.find(a => a.id === appId);
    if (app) {
      setEditingModal({
        appId,
        appName: app.application_name,
        currentValue: app.original_4r_recommendation,
        newValue: app.final_4r_model || '',
        reason: app.change_reason || '',
        licenseRenegotiation: app.license_renegotiation || false,
        consolidate: app.consolidate || false,
        consolidateTargetApp: app.consolidate_target_app || '',
        cloudMigration: app.cloud_migration || false,
        replaceApp: app.replace_app || false,
        replaceTargetApp: app.replace_target_app || ''
      });
    }
  };

  const handleModalSave = async () => {
    if (!editingModal) return;
    
    const { appId, newValue, reason, licenseRenegotiation, consolidate, consolidateTargetApp, cloudMigration, replaceApp, replaceTargetApp } = editingModal;

    // Check if the disposition value actually changed
    const currentApp = applications.find(a => a.id === appId);
    const dispositionChanged = currentApp?.final_4r_model !== newValue;

    // Validate reason is provided only when disposition changes
    if (dispositionChanged && !reason.trim()) {
      window.alert('Please provide a reason for changing the final disposition.');
      return;
    }

    if (reason.length > 300) {
      window.alert('Reason cannot exceed 300 characters.');
      return;
    }

    try {
      // Update local state immediately for better UX (optimistic update)
      const effectiveRenegotiation = newValue === 'Retain' ? licenseRenegotiation : false;
      const effectiveConsolidate = newValue === 'Re-Platform' ? consolidate : false;
      const effectiveCloudMigration = newValue === 'Re-Platform' ? cloudMigration : false;
      const effectiveReplace = newValue === 'Re-Platform' ? replaceApp : false;
      setApplications(prev => prev.map(app =>
        app.id === appId
          ? {
              ...app,
              final_4r_model: newValue as any,
              updated_at: new Date().toISOString(),
              updated_by: currentUser?.name || 'Unknown User',
              changed_by_user_id: currentUser?.id || 'unknown',
              change_reason: reason,
              license_renegotiation: effectiveRenegotiation,
              consolidate: effectiveConsolidate,
              consolidate_target_app: effectiveConsolidate ? consolidateTargetApp : '',
              cloud_migration: effectiveCloudMigration,
              replace_app: effectiveReplace,
              replace_target_app: effectiveReplace ? replaceTargetApp : ''
            }
          : app
      ));

      setEditingModal(null);

      // Make API call to persist the change
      const response = await authFetch(`/api/final-disposition/${appId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          final_4r_model: newValue || null,
          updated_by: currentUser?.name || 'Unknown User',
          changed_by_user_id: currentUser?.id || 'unknown',
          change_reason: reason,
          license_renegotiation: newValue === 'Retain' ? licenseRenegotiation : false,
          consolidate: newValue === 'Re-Platform' ? consolidate : false,
          consolidate_target_app: newValue === 'Re-Platform' && consolidate ? consolidateTargetApp : null,
          cloud_migration: newValue === 'Re-Platform' ? cloudMigration : false,
          replace_app: newValue === 'Re-Platform' ? replaceApp : false,
          replace_target_app: newValue === 'Re-Platform' && replaceApp ? replaceTargetApp : null
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update final disposition');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to update final disposition');
      }

      // Update local state with server response to ensure consistency, preserving change_reason
      setApplications(prev => prev.map(app => 
        app.id === appId ? { 
          ...app, 
          ...result.data,
          change_reason: result.data.change_reason || reason // Ensure change_reason is preserved
        } : app
      ));

      // Update stats - only reload stats, not all data to preserve the change_reason display
      const statsResponse = await authFetch('/api/final-disposition?page=1&limit=1');
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData.stats);
      }
      
    } catch (err: any) {
      console.error('Failed to update final disposition:', err);
      window.alert(`Error: ${err.message || 'Failed to update final disposition'}`);
      // Revert optimistic update by reloading data
      await loadDispositionData();
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleConfirmRetain = async (app: DispositionApplication) => {
    const confirmMessage = `Are you sure you want to confirm the Retain decision for application ${app.application_id_code} ${app.application_name}? This action cannot be undone and the application will be locked from further changes.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await authFetch(`/api/final-disposition/confirm-retain/${app.id}`, {
        method: 'POST',
        body: JSON.stringify({
          confirmed_by: currentUser?.name || 'Unknown User'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to confirm retain decision');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to confirm retain decision');
      }

      // Update the local state to reflect the confirmation
      setApplications(prevApps =>
        prevApps.map(prevApp =>
          prevApp.id === app.id
            ? { ...prevApp, ...result.data }
            : prevApp
        )
      );

      // Update stats - only reload stats, not all data
      const statsResponse = await authFetch('/api/final-disposition?page=1&limit=1');
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData.stats);
      }

      window.alert(`Successfully confirmed Retain decision for "${app.application_name}"!`);
    } catch (error: any) {
      console.error('Error confirming retain decision:', error);
      window.alert(`Error confirming retain decision: ${error.message}`);
    }
  };

  const handleTransferToIntegration = async (app: DispositionApplication) => {
    // Check if Final 4R Model is set
    if (!app.final_4r_model) {
      window.alert(`Cannot transfer "${app.application_name}" to Integration.\n\nPlease set the Final 4R Model first before transferring to Integration.`);
      return;
    }

    const confirmMessage = `Are you sure that you want to transfer application ${app.application_id_code} ${app.application_name} to Integration process. State that changes can not be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await authFetch(`/api/final-disposition/transfer-to-integration/${app.id}`, {
        method: 'POST',
        body: JSON.stringify({
          transferred_by: currentUser?.name || 'Unknown User'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to transfer to integration');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to transfer to integration');
      }

      // Update the specific application in state
      setApplications(prevApps =>
        prevApps.map(prevApp =>
          prevApp.id === app.id
            ? { ...prevApp, ...result.data }
            : prevApp
        )
      );

      window.alert(`Successfully transferred "${app.application_name}" to Integration!`);
    } catch (error: any) {
      console.error('Error transferring to integration:', error);
      window.alert(`Error transferring to integration: ${error.message}`);
    }
  };

  const openTransferModal = async () => {
    setTransferModal(prev => ({ ...prev, show: true, loading: true }));

    try {
      const response = await authFetch('/api/final-disposition/ready-for-transfer');
      if (!response.ok) {
        throw new Error('Failed to fetch applications ready for transfer');
      }

      const result = await response.json();
      
      if (result.success) {
        setTransferModal(prev => ({
          ...prev,
          applications: result.data,
          selectedApps: new Set(),
          loading: false
        }));
      } else {
        throw new Error(result.message || 'Failed to load ready applications');
      }
    } catch (error: any) {
      console.error('Error loading applications ready for transfer:', error);
      window.alert(`Error: ${error.message}`);
      setTransferModal(prev => ({ ...prev, show: false, loading: false }));
    }
  };

  const closeTransferModal = () => {
    setTransferModal({
      show: false,
      applications: [],
      selectedApps: new Set(),
      loading: false
    });
  };

  const toggleAppSelection = (appId: string) => {
    setTransferModal(prev => {
      const newSelected = new Set(prev.selectedApps);
      if (newSelected.has(appId)) {
        newSelected.delete(appId);
      } else {
        newSelected.add(appId);
      }
      return { ...prev, selectedApps: newSelected };
    });
  };

  const selectAllApps = () => {
    setTransferModal(prev => ({
      ...prev,
      selectedApps: new Set(prev.applications.map(app => app.application_id))
    }));
  };

  const clearAllApps = () => {
    setTransferModal(prev => ({
      ...prev,
      selectedApps: new Set()
    }));
  };

  const executeTransfer = async () => {
    const selectedCount = transferModal.selectedApps.size;
    if (selectedCount === 0) {
      window.alert('Please select at least one application to transfer.');
      return;
    }

    const confirmMessage = `Are you sure you want to transfer ${selectedCount} application(s) to Final 4R Disposition Model? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setTransferModal(prev => ({ ...prev, loading: true }));

    try {
      const response = await authFetch('/api/final-disposition/manual-transfer', {
        method: 'POST',
        body: JSON.stringify({
          applicationIds: Array.from(transferModal.selectedApps),
          transferredBy: currentUser?.name || 'Unknown User'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to execute manual transfer');
      }

      const result = await response.json();
      
      if (result.success) {
        window.alert(`Successfully transferred ${result.recordsAdded} applications to Final 4R Disposition Model!`);
        closeTransferModal();
        await loadDispositionData(); // Reload the main data to show new records
      } else {
        throw new Error(result.message || 'Failed to execute transfer');
      }
    } catch (error: any) {
      console.error('Error executing transfer:', error);
      window.alert(`Error: ${error.message}`);
    } finally {
      setTransferModal(prev => ({ ...prev, loading: false }));
    }
  };

  const downloadDispositionData = () => {
    if (applications.length === 0) {
      alert('No disposition data to download');
      return;
    }

    const headers = [
      'Application ID Code',
      'Application Name',
      'APR Model',
      'Original 4R Recommendation',
      'Overall Score',
      'Final Disposition',
      'Change Reason',
      'License Renegotiation',
      'Consolidate',
      'Consolidate Target App',
      'Cloud Migration',
      'Replace',
      'Replace Target',
      'Changed By',
      'Updated Date',
      'Transferred to Integration',
      'Transferred Date',
      'Transferred By'
    ];

    const csvRows = [headers.join(',')];

    applications.forEach(app => {
      const row = [
        `"${(app.application_id_code || '').replace(/"/g, '""')}"`,
        `"${(app.application_name || '').replace(/"/g, '""')}"`,
        `"${(app.apr_model_name || '').replace(/"/g, '""')}"`,
        `"${(app.original_4r_recommendation || '').replace(/"/g, '""')}"`,
        app.overall_score?.toFixed(2) || '',
        `"${(app.final_4r_model || '').replace(/"/g, '""')}"`,
        `"${(app.change_reason || '').replace(/"/g, '""')}"`,
        app.license_renegotiation ? 'Yes' : '',
        app.consolidate ? 'Yes' : '',
        `"${(app.consolidate_target_app || '').replace(/"/g, '""')}"`,
        app.cloud_migration ? 'Yes' : '',
        app.replace_app ? 'Yes' : '',
        `"${(app.replace_target_app || '').replace(/"/g, '""')}"`,
        `"${(app.updated_by || '').replace(/"/g, '""')}"`,
        app.updated_at ? new Date(app.updated_at).toLocaleDateString() : '',
        app.transferred_to_integration ? 'Yes' : 'No',
        app.transferred_at ? new Date(app.transferred_at).toLocaleDateString() : '',
        `"${(app.transferred_by || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `final_disposition_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Sort applications to move transferred ones to bottom and gray them out
  const sortedApplications = [...applications].sort((a, b) => {
    // Check if applications are locked (transferred to integration)
    const aLocked = a.transferred_to_integration;
    const bLocked = b.transferred_to_integration;

    // First sort by locked status - unlocked first
    if (aLocked !== bLocked) {
      return aLocked ? 1 : -1;
    }

    // Then by the current sort field
    const aValue = a[sortField as keyof typeof a] || '';
    const bValue = b[sortField as keyof typeof b] || '';
    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const getRecommendationColor = (recommendation: string | null) => {
    if (!recommendation) return '#6c757d';
    switch (recommendation) {
      case 'Retire': return '#dc2626';
      case 'Re-Platform': return '#f59e0b';
      case 'Re-Design': return '#3b82f6';
      case 'Retain': return '#10b981';
      default: return '#6c757d';
    }
  };

  const getRecommendationIcon = (recommendation: string | null) => {
    if (!recommendation) return '❓';
    switch (recommendation) {
      case 'Retire': return '🗑️';
      case 'Re-Platform': return '🔄';
      case 'Re-Design': return '🎨';
      case 'Retain': return '✅';
      default: return '❓';
    }
  };

  const computedStats = React.useMemo(() => {
    const completionRate = stats.totalApplications > 0 
      ? Math.round((stats.completedDispositions / stats.totalApplications) * 100) 
      : 0;
    
    return {
      total: stats.totalApplications,
      pending: stats.pendingDispositions,
      completed: stats.completedDispositions,
      completionRate
    };
  }, [stats]);

  if (loading) {
    return (
      <div className="disposition-loading">
        <div className="loading-spinner"></div>
        <p>Loading disposition data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="disposition-error">
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        <button onClick={loadDispositionData} className="btn-retry">Retry</button>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Final Application Disposition Model</h1>
            <p className="page-header__subtitle">
              Override initial 4R recommendations while preserving original scoring integrity
            </p>
          </div>
          <div className="page-header__actions">
            {applications.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadDispositionData}
                title="Download current disposition data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">
        {/* Statistics - Compact */}
        <div className="disposition-stats-compact">
          <span className="stats-title">📊 Statistics:</span>
          <div className="stats-items">
            <div className="stat-item-compact">
              <span className="stat-value-compact">{computedStats.total}</span>
              <span className="stat-label-compact">Total</span>
            </div>
            <div className="stat-item-compact">
              <span className="stat-value-compact" style={{ color: '#4CAF50' }}>{computedStats.completed}</span>
              <span className="stat-label-compact">Disposition Set</span>
            </div>
            <div className="stat-item-compact">
              <span className="stat-value-compact" style={{ color: '#FF9800' }}>{computedStats.pending}</span>
              <span className="stat-label-compact">Pending</span>
            </div>
            <div className="stat-item-compact">
              <span className="stat-value-compact" style={{ color: '#9C27B0' }}>{computedStats.completionRate}%</span>
              <span className="stat-label-compact">Completion</span>
            </div>
          </div>
        </div>


        {/* Filters and Search */}
        <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
            {/* Search Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1.5 }}>
              <label style={{ fontSize: '14px', fontWeight: '600', minWidth: '70px' }}>Search:</label>
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '300px',
                  flex: 1,
                  maxWidth: '450px'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    padding: '6px 10px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Original Recommendation Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Original:</label>
              <select
                value={filterOriginal}
                onChange={(e) => setFilterOriginal(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '140px'
                }}
              >
                <option value="All">All</option>
                <option value="Retire">🗑️ Retire</option>
                <option value="Re-Platform">🔄 Re-Platform</option>
                <option value="Re-Design">🎨 Re-Design</option>
                <option value="Retain">✅ Retain</option>
              </select>
            </div>

            {/* Final Disposition Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Final:</label>
              <select
                value={filterFinal}
                onChange={(e) => setFilterFinal(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '140px'
                }}
              >
                <option value="All">All</option>
                <option value="Pending">❓ Pending</option>
                <option value="Retire">🗑️ Retire</option>
                <option value="Re-Platform">🔄 Re-Platform</option>
                <option value="Re-Design">🎨 Re-Design</option>
                <option value="Retain">✅ Retain</option>
              </select>
            </div>

            {/* Page Size */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>Show:</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>

            {/* Expand/Collapse Controls */}
            <div className="expand-collapse-controls">
              <button
                className="icon-btn expand-btn"
                onClick={handleExpandAll}
                title="Expand All"
              >
                <span className="icon-arrow">▼</span>
                <span className="icon-label">Expand All</span>
              </button>
              <button
                className="icon-btn collapse-btn"
                onClick={handleCollapseAll}
                title="Collapse All"
              >
                <span className="icon-arrow">▲</span>
                <span className="icon-label">Collapse All</span>
              </button>
            </div>
          </div>
        </div>

        {/* Results Info */}
        <div className="results-info">
          <span>Showing {applications.length} of {totalCount} applications</span>
          {(searchTerm || filterFinal !== 'All') && totalCount !== computedStats.total && (
            <span className="filtered-note">(filtered from {computedStats.total} total)</span>
          )}
        </div>

        {/* Data Table - Grouped by APR Model */}
        <div className="model-groups-container">
          {modelGroups.length === 0 ? (
            <div className="empty-state-container">
              <h3>No Applications Found</h3>
              <p>No applications match the current filter criteria.</p>
            </div>
          ) : (
            modelGroups.map(group => {
              const isCollapsed = collapsedModels.has(group.modelId);
              const completedCount = group.applications.filter(app => app.final_4r_model).length;
              const pendingCount = group.applications.length - completedCount;

              return (
                <div key={group.modelId} className="model-group">
                  {/* Model Header Row */}
                  <div
                    className="model-header-row"
                    onClick={() => toggleModelCollapse(group.modelId)}
                  >
                    <button
                      className="collapse-toggle-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleModelCollapse(group.modelId);
                      }}
                      title={isCollapsed ? 'Expand' : 'Collapse'}
                    >
                      {isCollapsed ? '▶' : '▼'}
                    </button>
                    <span className="model-header-icon">📁</span>
                    <div className="model-header-info">
                      <span className="model-header-name">{group.modelName}</span>
                    </div>
                    <div className="model-header-stats">
                      <span className="model-stats-badge apps">
                        {group.applications.length} apps
                      </span>
                      <span className="model-stats-badge completed">
                        {completedCount} completed
                      </span>
                      {pendingCount > 0 && (
                        <span className="model-stats-badge pending">
                          {pendingCount} pending
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Applications Table - Collapsible */}
                  {!isCollapsed && (
                    <div className="table-container">
                      <table className="disposition-table">
                        <thead>
                          <tr>
                            <th onClick={() => handleSort('application_name')} className="sortable" style={{ width: '42%' }}>
                              Application {sortField === 'application_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('original_4r_recommendation')} className="sortable center col-original-4r" style={{ width: '10%' }}>
                              <span>Original</span><br/><span>Recommendation</span> {sortField === 'original_4r_recommendation' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => handleSort('overall_score')} className="sortable center" style={{ width: '8%' }}>
                              Score {sortField === 'overall_score' && (sortDirection === 'asc' ? '↑' : '↓')}
                            </th>
                            <th className="center editable-header" style={{ width: '10%' }}>
                              FINAL DISPOSITION <span className="editable-indicator">✏️</span>
                            </th>
                            <th className="center" style={{ width: '10%' }}>
                              Changed By
                            </th>
                            <th className="center" style={{ width: '10%' }}>
                              Updated
                            </th>
                            <th className="center" style={{ width: '10%' }}>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.applications.map((app) => (
                            <tr
                              key={app.id}
                              className={app.transferred_to_integration ? 'transferred-row' : ''}
                              style={{
                                opacity: app.transferred_to_integration ? 0.5 : 1,
                                backgroundColor: app.transferred_to_integration ? '#f8f9fa' : 'transparent'
                              }}
                            >
                              <td className="app-info-cell">
                                <div className="md-app-info">
                                  <div className="md-app-info__primary">{app.application_id_code}</div>
                                  <div className="md-app-info__primary">{app.application_name}</div>
                                </div>
                              </td>
                              <td className="center col-original-4r">
                                <div
                                  className="recommendation-badge original compact"
                                  style={{ backgroundColor: getRecommendationColor(app.original_4r_recommendation) }}
                                >
                                  {app.original_4r_recommendation}
                                </div>
                              </td>
                              <td className="center">
                                <div className="score-display">
                                  <span className="score-value">{app.overall_score.toFixed(2)}</span>
                                  <div className="score-bar">
                                    <div
                                      className="score-fill"
                                      style={{
                                        width: `${(app.overall_score / 5) * 100}%`,
                                        backgroundColor: getRecommendationColor(app.original_4r_recommendation)
                                      }}
                                    />
                                  </div>
                                </div>
                              </td>
                              <td className="center">
                                <div
                                  className={`recommendation-badge final ${app.final_4r_model ? 'set' : 'pending'}`}
                                  style={{
                                    backgroundColor: getRecommendationColor(app.final_4r_model),
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => {
                                    if (app.transferred_to_integration) {
                                      window.alert('This application has been transferred to integration and cannot be modified.');
                                      return;
                                    }
                                    openEditingModal(app.id);
                                  }}
                                  title="Click to edit final disposition (reason required)"
                                >
                                  {app.final_4r_model ? (
                                    <>
                                      {getRecommendationIcon(app.final_4r_model)} {app.final_4r_model}
                                    </>
                                  ) : (
                                    '❓ Click to Set'
                                  )}
                                </div>
                                {app.license_renegotiation && (
                                  <div style={{ marginTop: '4px' }}>
                                    <span style={{
                                      padding: '2px 8px', fontSize: '10px', fontWeight: '600',
                                      backgroundColor: '#fff3e0', color: '#e65100', borderRadius: '12px',
                                      border: '1px solid #ffcc80'
                                    }}>
                                      License Renegotiation
                                    </span>
                                  </div>
                                )}
                                {app.consolidate && (
                                  <div style={{ marginTop: '4px' }}>
                                    <span style={{
                                      padding: '2px 8px', fontSize: '10px', fontWeight: '600',
                                      backgroundColor: '#f3e8ff', color: '#7c3aed', borderRadius: '12px',
                                      border: '1px solid #d8b4fe'
                                    }}>
                                      Consolidate{app.consolidate_target_app ? ` → ${app.consolidate_target_app}` : ''}
                                    </span>
                                  </div>
                                )}
                                {app.cloud_migration && (
                                  <div style={{ marginTop: '4px' }}>
                                    <span style={{
                                      padding: '2px 8px', fontSize: '10px', fontWeight: '600',
                                      backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: '12px',
                                      border: '1px solid #7dd3fc'
                                    }}>
                                      Cloud Migration
                                    </span>
                                  </div>
                                )}
                                {app.replace_app && (
                                  <div style={{ marginTop: '4px' }}>
                                    <span style={{
                                      padding: '2px 8px', fontSize: '10px', fontWeight: '600',
                                      backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '12px',
                                      border: '1px solid #fbbf24'
                                    }}>
                                      Replace{app.replace_target_app ? ` → ${app.replace_target_app}` : ''}
                                    </span>
                                  </div>
                                )}
                                {app.change_reason && (
                                  <div className="change-reason-indicator" title={`Reason: ${app.change_reason}`}>
                                    💬
                                  </div>
                                )}
                              </td>
                              <td className="center">
                                <div className="changed-by-info">
                                  {app.updated_by && app.updated_by !== 'system' ? (
                                    <div className="changed-by-user">{app.updated_by}</div>
                                  ) : (
                                    <span className="no-changes">-</span>
                                  )}
                                </div>
                              </td>
                              <td className="center">
                                <div className="update-info">
                                  <div className="update-date">
                                    {new Date(app.updated_at).toLocaleDateString()}
                                  </div>
                                </div>
                              </td>
                              <td className="center">
                                <div className="action-buttons">
                                  {!app.transferred_to_integration ? (
                                    <button
                                      className="transfer-btn"
                                      onClick={() => handleTransferToIntegration(app)}
                                    >
                                      🚀 Transfer to Integration
                                    </button>
                                  ) : (
                                    <div className="transferred-status">
                                      ✅ Transferred
                                      <div style={{ fontSize: '10px', color: 'rgba(0,0,0,0.6)' }}>
                                        {app.transferred_at && new Date(app.transferred_at).toLocaleDateString()}
                                      </div>
                                      {app.transferred_by && (
                                        <div style={{ fontSize: '10px', color: 'rgba(0,0,0,0.6)' }}>
                                          by {app.transferred_by}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="page-btn"
            >
              First
            </button>
            <button 
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="page-btn"
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="page-btn"
            >
              Next
            </button>
            <button 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="page-btn"
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      <div>
      {/* Edit Modal - Clean White Style */}
      {editingModal && (
        <div className="modal-overlay" onClick={() => setEditingModal(null)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '480px',
              maxWidth: '560px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                Edit Final Disposition
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                Update the final disposition for this application
              </p>
            </div>

            {/* Form Fields */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333'
              }}>
                Application
              </label>
              <div style={{
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#333'
              }}>
                {editingModal.appName}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333'
              }}>
                Original 4R Recommendation
              </label>
              <div style={{
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {editingModal.currentValue ? (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    backgroundColor: getRecommendationColor(editingModal.currentValue),
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}>
                    {getRecommendationIcon(editingModal.currentValue)} {editingModal.currentValue}
                  </span>
                ) : (
                  <span style={{ color: '#999' }}>Not set</span>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="new-disposition" style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333'
              }}>
                New Final Disposition *
              </label>
              <select
                id="new-disposition"
                value={editingModal.newValue}
                onChange={(e) => setEditingModal(prev => prev ? {...prev, newValue: e.target.value} : null)}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select Final Disposition</option>
                <option value="Retire">🗑️ Retire</option>
                <option value="Re-Platform">🔄 Re-Platform</option>
                <option value="Re-Design">🎨 Re-Design</option>
                <option value="Retain">✅ Retain</option>
              </select>
            </div>

            {/* License Renegotiation checkbox — only visible when Retain is selected */}
            {/* Retain options */}
            {editingModal.newValue === 'Retain' && (
              <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#1565c0' }}>
                  <input
                    type="checkbox"
                    checked={editingModal.licenseRenegotiation}
                    onChange={(e) => setEditingModal(prev => prev ? { ...prev, licenseRenegotiation: e.target.checked } : null)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  License Renegotiation Eligible
                </label>
                <p style={{ margin: '6px 0 0 28px', fontSize: '12px', color: '#666' }}>
                  Flag this application for license cost renegotiation.
                </p>
              </div>
            )}

            {/* Re-Platform options */}
            {editingModal.newValue === 'Re-Platform' && (
              <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f3e8ff', borderRadius: '8px', border: '1px solid #d8b4fe' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#7c3aed', marginBottom: '10px' }}>
                  <input
                    type="checkbox"
                    checked={editingModal.consolidate}
                    onChange={(e) => setEditingModal(prev => prev ? { ...prev, consolidate: e.target.checked } : null)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Consolidate
                </label>
                {editingModal.consolidate && (
                  <div style={{ marginLeft: '28px', marginBottom: '10px' }}>
                    <input
                      type="text"
                      placeholder="Target application (e.g., IT.443 Smartsheet)"
                      value={editingModal.consolidateTargetApp}
                      onChange={(e) => setEditingModal(prev => prev ? { ...prev, consolidateTargetApp: e.target.value } : null)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d8b4fe', borderRadius: '6px', fontSize: '13px' }}
                    />
                  </div>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#7c3aed', marginBottom: '10px' }}>
                  <input
                    type="checkbox"
                    checked={editingModal.cloudMigration}
                    onChange={(e) => setEditingModal(prev => prev ? { ...prev, cloudMigration: e.target.checked } : null)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Cloud Migration
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#7c3aed' }}>
                  <input
                    type="checkbox"
                    checked={editingModal.replaceApp}
                    onChange={(e) => setEditingModal(prev => prev ? { ...prev, replaceApp: e.target.checked } : null)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  Replace
                </label>
                {editingModal.replaceApp && (
                  <div style={{ marginLeft: '28px', marginTop: '6px' }}>
                    <input
                      type="text"
                      placeholder="Replacement product (e.g., Modern EDI SaaS)"
                      value={editingModal.replaceTargetApp}
                      onChange={(e) => setEditingModal(prev => prev ? { ...prev, replaceTargetApp: e.target.value } : null)}
                      style={{ width: '100%', padding: '8px', border: '1px solid #d8b4fe', borderRadius: '6px', fontSize: '13px' }}
                    />
                  </div>
                )}
              </div>
            )}

            <div style={{ marginBottom: '24px' }}>
              <label htmlFor="change-reason" style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#333'
              }}>
                Reason for Change *
              </label>
              <textarea
                id="change-reason"
                value={editingModal.reason}
                onChange={(e) => setEditingModal(prev => prev ? {...prev, reason: e.target.value} : null)}
                placeholder="Please explain why this change is necessary..."
                maxLength={300}
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              <div style={{
                marginTop: '6px',
                fontSize: '12px',
                color: '#999',
                textAlign: 'right'
              }}>
                {editingModal.reason.length}/300 characters
              </div>
            </div>

            {/* Footer Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              paddingTop: '16px',
              borderTop: '1px solid #eee'
            }}>
              <button
                onClick={() => setEditingModal(null)}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleModalSave}
                disabled={!editingModal.newValue || ((() => { const app = applications.find(a => a.id === editingModal.appId); return app?.final_4r_model !== editingModal.newValue; })() && !editingModal.reason.trim())}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: (!editingModal.newValue || !editingModal.reason.trim()) ? '#ccc' : '#1976d2',
                  color: 'white',
                  cursor: (!editingModal.newValue || !editingModal.reason.trim()) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {transferModal.show && (
        <div className="modal-overlay" onClick={closeTransferModal}>
          <div className="transfer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Move Applications to Final 4R</h2>
              <button className="modal-close" onClick={closeTransferModal}>×</button>
            </div>
            
            <div className="modal-body">
              {transferModal.loading ? (
                <div className="modal-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading applications ready for transfer...</p>
                </div>
              ) : transferModal.applications.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📋</div>
                  <h3>No Applications Ready</h3>
                  <p>All completed applications have already been transferred to Final 4R Disposition Model.</p>
                </div>
              ) : (
                <>
                  <div className="transfer-summary">
                    <p>Found <strong>{transferModal.applications.length}</strong> applications with completed scoring ready for transfer.</p>
                    <div className="selection-controls">
                      <button 
                        onClick={selectAllApps}
                        className="btn-select-action"
                        disabled={transferModal.loading}
                      >
                        Select All
                      </button>
                      <button 
                        onClick={clearAllApps}
                        className="btn-select-action"
                        disabled={transferModal.loading}
                      >
                        Clear All
                      </button>
                      <span className="selection-count">
                        {transferModal.selectedApps.size} selected
                      </span>
                    </div>
                  </div>

                  <div className="applications-list">
                    {transferModal.applications.map(app => (
                      <div key={app.application_id} className="application-item">
                        <label className="application-checkbox">
                          <input
                            type="checkbox"
                            checked={transferModal.selectedApps.has(app.application_id)}
                            onChange={() => toggleAppSelection(app.application_id)}
                            disabled={transferModal.loading}
                          />
                          <span className="checkmark"></span>
                        </label>
                        
                        <div className="application-details">
                          <strong className="app-id">{app.application_id_code}</strong>
                          <span className="app-name">{app.application_name}</span>
                          <span className="recommendation-badge" style={{ 
                            backgroundColor: getRecommendationColor(app.recommendation),
                            color: 'white'
                          }}>
                            {getRecommendationIcon(app.recommendation)} {app.recommendation}
                          </span>
                          <span className="app-completed">Completed: {app.completed_at_formatted}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {!transferModal.loading && transferModal.applications.length > 0 && (
              <div className="modal-footer">
                <button 
                  className="btn-cancel"
                  onClick={closeTransferModal}
                  disabled={transferModal.loading}
                >
                  Cancel
                </button>
                <button 
                  className="btn-transfer-execute"
                  onClick={executeTransfer}
                  disabled={transferModal.loading || transferModal.selectedApps.size === 0}
                >
                  {transferModal.loading ? 'Transferring...' : `Transfer ${transferModal.selectedApps.size} Application(s)`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default FinalApplicationDispositionModel;